// Lifted directly from @atlaskit/flag.

/** @jsx jsx */
import { MouseEventHandler, useCallback } from 'react';
import { css, jsx } from '@emotion/core';

import CrossIcon from '@atlaskit/icon/glyph/cross';
import { N0, N50A, N60A, N800, TrelloBlue500 } from '@trello/colors';

import { FlagProps } from './types';

import { FlagActions } from './FlagActions';
import { useFlagGroup } from './FlagGroup';

function noop() {}

const gridSize = 8;
const doubleGridSize = gridSize * 2;
const headerHeight = gridSize * 4;
const paddingLeft = gridSize * 5;

export const Flag = (props: FlagProps) => {
  const {
    actions = [],
    icon,
    title,
    description,
    isUndismissable,
    onMouseOver,
    onFocus = noop,
    onMouseOut,
    onBlur = noop,
    testId,
    id,
  } = props;

  const { onDismissed = noop, dismissAllowed } = useFlagGroup();
  const isDismissAllowed = !isUndismissable && dismissAllowed(id);

  const renderToggleOrDismissButton = () => {
    // If it is normal appearance a toggle button cannot be rendered
    // Ensure onDismissed is defined and isDismissAllowed is true to render
    // the dismiss button
    if (!isDismissAllowed) {
      return null;
    }

    const ButtonIcon = CrossIcon;
    const buttonLabel = 'Dismiss flag';
    const buttonAction = () => {
      if (isDismissAllowed) {
        onDismissed(id);
      }
    };
    const size: 'small' | 'large' = 'small';
    const buttonTestId = testId && `${testId}-dismiss`;
    return (
      <button
        css={css`
          appearance: none;
          background: none;
          border: none;
          border-radius: 3px;
          color: ${N800};
          cursor: pointer;
          flex: 0 0 auto;
          line-height: 1;
          margin-left: ${gridSize}px;
          padding: 0;
          white-space: nowrap;
          &:focus {
            outline: none;
            box-shadow: 0 0 0 2px ${TrelloBlue500};
          }
        `}
        onClick={buttonAction}
        data-testid={buttonTestId}
        type="button"
      >
        <ButtonIcon label={buttonLabel} size={size} />
      </button>
    );
  };

  // We prevent default on mouse down to avoid focus ring when the flag is clicked,
  // while still allowing it to be focused with the keyboard.
  const handleMouseDown: MouseEventHandler<HTMLElement> = useCallback((e) => {
    e.preventDefault();
  }, []);

  const autoDismissProps = {
    onMouseOver,
    onFocus,
    onMouseOut,
    onBlur,
  };
  const OptionalDismissButton = renderToggleOrDismissButton;

  const boxShadow = `0 0 1px ${N60A}, 0 20px 32px -8px ${N50A}`;

  // const mode = tokens.mode;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      css={css`
        background-color: ${N0};
        border-radius: 3px;
        box-sizing: border-box;
        box-shadow: ${boxShadow};
        color: ${N800};
        padding: ${doubleGridSize}px;
        transition: background-color 200ms;
        width: 100%;
        z-index: 600;

        &:focus {
          outline: none;
          box-shadow: 0 0 0 2px ${TrelloBlue500};
        }
      `}
      role="alert"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onMouseDown={handleMouseDown}
      data-testid={testId}
      {...autoDismissProps}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          min-height: ${headerHeight}px;
        `}
      >
        {icon}
        <span
          css={css`
            color: ${N800};
            font-weight: 600;
            flex: 1;
            padding: 0 0 0 ${doubleGridSize}px;
          `}
        >
          {title}
        </span>
        <OptionalDismissButton />
      </div>
      {/* Meant to imitate the Expander component in @atlaskit/flag. */}
      <div
        css={css`
          display: flex;
          flex: 1 1 100%;
          flex-direction: column;
          justify-content: center;
          min-width: 0;
          padding-left: ${paddingLeft}px;
        `}
      >
        {description && (
          <div
            css={css`
              color: ${N800};
              word-wrap: break-word;
              overflow: auto;
            `}
            data-testid={testId && `${testId}-description`}
          >
            {description}
          </div>
        )}
        <FlagActions actions={actions} testId={testId} />
      </div>
    </div>
  );
};
